import type * as MonacoApi from "monaco-editor/editor";
import type { Awareness } from "y-protocols/awareness";
import * as Y from "yjs";

type Monaco = typeof MonacoApi;

/** What a user is, as published in awareness under `user`. */
export interface AwarenessUser {
  name: string;
  /** Catppuccin accent name (rendered through CSS variables, so it follows the theme). */
  color: string;
  clientId: string;
}

/** Published under `cursor`: every selection in the file the user has open. */
export interface AwarenessCursor {
  fileId: string;
  /** Y.RelativePosition JSON, so positions survive concurrent edits. */
  selections: { anchor: unknown; head: unknown }[];
}

/** Hide a remote user's name label after this long without movement. */
const LABEL_IDLE_MS = 3_000;
const STYLE_ELEMENT_ID = "codeshare-remote-cursor-labels";

const cssString = (value: string) =>
  `"${value.replace(/["\\\n]/g, (c) => (c === "\n" ? " " : `\\${c}`))}"`;
const safeAccent = (color: string) => (/^[a-z]+$/.test(color) ? color : "overlay2");

/**
 * Publishes this editor's selections (all of them, for multi-cursor) to awareness, and renders
 * every remote user's selections and carets with their color and a name label that fades when idle.
 */
export class RemoteCursors {
  readonly #monaco: Monaco;
  readonly #editor: MonacoApi.editor.ICodeEditor;
  readonly #awareness: Awareness;
  readonly #ytext: Y.Text;
  readonly #fileId: string;
  readonly #decorations: MonacoApi.editor.IEditorDecorationsCollection;
  /** When each remote user last moved, and what their selections were then (for idle labels). */
  readonly #lastMoved = new Map<number, number>();
  readonly #lastKeys = new Map<number, string>();
  readonly #disposables: MonacoApi.IDisposable[] = [];
  readonly #timer: ReturnType<typeof setInterval>;
  readonly #onAwarenessChange: () => void;
  readonly #onTextChange: () => void;

  constructor(options: {
    monaco: Monaco;
    editor: MonacoApi.editor.ICodeEditor;
    awareness: Awareness;
    ytext: Y.Text;
    fileId: string;
  }) {
    this.#monaco = options.monaco;
    this.#editor = options.editor;
    this.#awareness = options.awareness;
    this.#ytext = options.ytext;
    this.#fileId = options.fileId;
    this.#decorations = this.#editor.createDecorationsCollection();

    this.#disposables.push(this.#editor.onDidChangeCursorSelection(() => this.#publish()));
    this.#onAwarenessChange = () => this.#render();
    this.#awareness.on("change", this.#onAwarenessChange);
    // Remote text edits move positions: re-render.
    this.#onTextChange = () => this.#render();
    this.#ytext.observe(this.#onTextChange);
    // Fade labels of idle users.
    this.#timer = setInterval(() => this.#render(), 1_000);
    this.#publish();
    this.#render();
  }

  dispose(): void {
    for (const d of this.#disposables) d.dispose();
    this.#awareness.off("change", this.#onAwarenessChange);
    this.#ytext.unobserve(this.#onTextChange);
    clearInterval(this.#timer);
    this.#decorations.clear();
    const local = this.#awareness.getLocalState() as { cursor?: AwarenessCursor } | null;
    if (local?.cursor?.fileId === this.#fileId) this.#awareness.setLocalStateField("cursor", null);
  }

  #publish(): void {
    const model = this.#editor.getModel();
    const selections = this.#editor.getSelections();
    if (!model || !selections) return;
    const relative = (position: MonacoApi.IPosition) =>
      Y.relativePositionToJSON(
        Y.createRelativePositionFromTypeIndex(this.#ytext, model.getOffsetAt(position)),
      );
    const cursor: AwarenessCursor = {
      fileId: this.#fileId,
      selections: selections.map((s) => ({
        anchor: relative(s.getSelectionStart()), // where the selection started
        head: relative(s.getPosition()), // where the caret is
      })),
    };
    this.#awareness.setLocalStateField("cursor", cursor);
  }

  #render(): void {
    const model = this.#editor.getModel();
    const doc = this.#ytext.doc;
    if (!model || !doc) return;
    const now = Date.now();
    const decorations: MonacoApi.editor.IModelDeltaDecoration[] = [];
    const labels: string[] = [];

    for (const [clientId, state] of this.#awareness.getStates()) {
      if (clientId === doc.clientID) continue;
      const { user, cursor } = state as { user?: AwarenessUser; cursor?: AwarenessCursor | null };
      if (!user || !cursor || cursor.fileId !== this.#fileId) continue;

      const key = JSON.stringify(cursor.selections);
      const previous = this.#lastMoved.get(clientId);
      if (previous === undefined || (this.#lastKeys.get(clientId) ?? "") !== key) {
        this.#lastMoved.set(clientId, now);
        this.#lastKeys.set(clientId, key);
      }
      const idle = now - (this.#lastMoved.get(clientId) ?? now) > LABEL_IDLE_MS;
      const accent = safeAccent(user.color);
      labels.push(`.cs-remote-label-${clientId}::after { content: ${cssString(user.name)}; }`);

      cursor.selections.forEach((selection, index) => {
        const anchor = this.#absolute(selection.anchor, doc);
        const head = this.#absolute(selection.head, doc);
        if (anchor === undefined || head === undefined) return;
        const anchorPos = model.getPositionAt(anchor);
        const headPos = model.getPositionAt(head);
        if (anchor !== head) {
          const [start, end] = anchor < head ? [anchorPos, headPos] : [headPos, anchorPos];
          decorations.push({
            range: new this.#monaco.Range(
              start.lineNumber,
              start.column,
              end.lineNumber,
              end.column,
            ),
            options: { className: `cs-remote-selection cs-peer-${accent}`, stickiness: 1 },
          });
        }
        // Label only on the primary caret, and only while the user is active.
        const label =
          index === 0 && !idle
            ? ` cs-remote-label cs-remote-label-${clientId}${headPos.lineNumber === 1 ? " cs-remote-label-below" : ""}`
            : "";
        decorations.push({
          range: new this.#monaco.Range(
            headPos.lineNumber,
            headPos.column,
            headPos.lineNumber,
            headPos.column,
          ),
          options: {
            beforeContentClassName: `cs-remote-caret cs-peer-${accent}${label}`,
            stickiness: 1,
          },
        });
      });
    }
    this.#decorations.set(decorations);
    setLabelStyles(labels);
  }

  #absolute(json: unknown, doc: Y.Doc): number | undefined {
    try {
      const position = Y.createAbsolutePositionFromRelativePosition(
        Y.createRelativePositionFromJSON(json),
        doc,
      );
      return position && position.type === this.#ytext ? position.index : undefined;
    } catch {
      return undefined;
    }
  }
}

/** Name labels need per-user text, which CSS can only get from a rule per client. */
function setLabelStyles(rules: string[]): void {
  if (typeof document === "undefined") return;
  let style = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ELEMENT_ID;
    document.head.append(style);
  }
  const css = rules.join("\n");
  if (style.textContent !== css) style.textContent = css;
}
